import { getCapitalGlobalDocumentError } from '@/features/quotes/utils/capital-global-document-error.util';
import type {
  CapitalGlobalCoverageCatalogItem,
  PerfilDoGrupo,
  TipoDePerfil,
} from '@/features/quotes/schemas/capital-global';
import {
  getCapitalBelowMinMessage,
  getCapitalExceedsMaxMessage,
  type MorteRiskStepLimits,
} from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import type {
  EntityRiskValues,
  EntityTab,
  RiskDataMap,
  SubStipulantEntry,
} from '../../types';
import {
  parseNum,
  RISK_STEP_MAX_INDIVIDUAL_CAPITAL_BY_BAND,
  RISK_STEP_MAX_LIVES_VALUE,
  RISK_STEP_MAX_NUMERIC_VALUE,
  RISK_STEP_MAX_TOTAL_LIVES,
  RISK_STEP_MIN_INDIVIDUAL_CAPITAL,
  type CapitalBand,
} from './step-helpers.util';
import { formatCurrency } from '@/shared/utils/currencyFormatter';

export const DUPLICATE_CAPITAL_GLOBAL_DOCUMENT_MESSAGE =
  'Este documento já foi informado.';

export type SubStipulantFieldErrors = {
  documentNumber?: string;
  name?: string;
  segment?: string;
  cnae?: string;
};

type ValidateSubStipulantsOptions = {
  stipulantDocumentNumber?: string;
};

export function validateSubStipulants(
  entries: SubStipulantEntry[],
  options?: ValidateSubStipulantsOptions
): Record<string, SubStipulantFieldErrors> {
  const stipulantClean = removeFormatRegisterNumber(
    options?.stipulantDocumentNumber ?? ''
  );
  const seenSubDocuments = new Set<string>();
  const out: Record<string, SubStipulantFieldErrors> = {};

  for (const entry of entries) {
    const doc = (entry.documentNumber || '').trim();
    const nm = (entry.name || '').trim();
    const isIndividual = entry.useIndividualDocument;
    const err: SubStipulantFieldErrors = {};

    const docError = getCapitalGlobalDocumentError(doc, Boolean(isIndividual));
    if (docError) {
      err.documentNumber = docError;
    }

    if (!err.documentNumber && doc) {
      const cleanDoc = removeFormatRegisterNumber(doc);
      if (cleanDoc) {
        if (stipulantClean && cleanDoc === stipulantClean) {
          err.documentNumber = DUPLICATE_CAPITAL_GLOBAL_DOCUMENT_MESSAGE;
        } else if (seenSubDocuments.has(cleanDoc)) {
          err.documentNumber = DUPLICATE_CAPITAL_GLOBAL_DOCUMENT_MESSAGE;
        } else {
          seenSubDocuments.add(cleanDoc);
        }
      }
    }

    if (!nm) {
      err.name = isIndividual
        ? 'Nome é obrigatório'
        : 'Razão social é obrigatória';
    }

    if (!(entry.segment || '').trim()) {
      err.segment = 'Segmento é obrigatório';
    }

    if (!(entry.cnae || '').trim()) {
      err.cnae = 'CNAE é obrigatório';
    }

    if (err.documentNumber || err.name || err.segment || err.cnae) {
      out[entry.id] = err;
    }
  }

  return out;
}

function getMaxLivesValueMessage(): string {
  return `Quantidade de vidas não pode ser maior que ${RISK_STEP_MAX_LIVES_VALUE.toLocaleString('pt-BR')}.`;
}

function getMaxNumericValueMessage(): string {
  return `O valor não pode ser maior que ${formatCurrency(RISK_STEP_MAX_NUMERIC_VALUE)}.`;
}

export function getQuantidadeDeVidasValidationError(
  value: string
): string | undefined {
  if (!value || parseNum(value) <= 0) {
    return 'Quantidade de vidas é obrigatória';
  }

  if (parseNum(value) > RISK_STEP_MAX_LIVES_VALUE) {
    return getMaxLivesValueMessage();
  }

  return undefined;
}

export function getIndividualCapitalValidationError(
  value: string
): string | undefined {
  if (!value || parseNum(value) <= 0) {
    return 'Capital individual é obrigatório';
  }

  if (parseNum(value) > RISK_STEP_MAX_NUMERIC_VALUE) {
    return getMaxNumericValueMessage();
  }

  return undefined;
}

export type MorteRiskValidationContext = {
  morteItem: CapitalGlobalCoverageCatalogItem | undefined;
  limits: MorteRiskStepLimits | undefined;
};

export type IndividualCapitalRange = {
  min: number;
  max: number;
};

/**
 * Banda do perfil UNIFORME, a partir do Tipo de Perfil.
 *
 * "Colaboradores + Sócios" usa o teto de colaboradores — o mais restritivo —
 * porque o grupo inclui colaboradores. É a inversa do que
 * `map-risk-values-to-payload.util.ts` faz ao montar o payload.
 *
 * Só vale para o UNIFORME: no ESCALONADO a banda vem do nome do campo, porque
 * `tipoDePerfil` guarda valor obsoleto ali.
 */
export function resolveCapitalBandFromTipoDePerfil(
  tipoDePerfil: TipoDePerfil
): CapitalBand {
  return tipoDePerfil === 'SOCIOS' ? 'partnersAndDirectors' : 'employees';
}

/**
 * Faixa de Capital Individual que vale de fato.
 *
 * O TETO é a regra comercial da banda, e só ela: o `SumInsuredMax` da Morte no
 * catálogo NÃO corta mais o Capital Individual. Enquanto cortava, um catálogo
 * defasado derrubava em silêncio o teto que o corretor tinha sido informado — e
 * pior, só depois que a query do catálogo respondia: o campo anunciava o teto
 * comercial na primeira montagem e trocava para o do catálogo ao voltar ao step,
 * com o valor já digitado virando inválido sem nada na tela ter mudado.
 *
 * O PISO segue cruzado: um mínimo do catálogo acima do piso comercial é exigência
 * do produto que a tela não tem como afrouxar.
 */
export function resolveEffectiveIndividualCapitalRange(
  limits: MorteRiskStepLimits | undefined,
  band: CapitalBand
): IndividualCapitalRange {
  return {
    min: Math.max(RISK_STEP_MIN_INDIVIDUAL_CAPITAL, limits?.min ?? 0),
    max: RISK_STEP_MAX_INDIVIDUAL_CAPITAL_BY_BAND[band],
  };
}

/**
 * Teto comercial isolado — NÃO depende do catálogo.
 *
 * `getIndividualCapitalMorteLimitError` devolve `undefined` enquanto o catálogo
 * não carregou; a regra comercial não pode ficar refém disso.
 */
export function getIndividualCapitalMaxAllowedError(
  value: string,
  band: CapitalBand
): string | undefined {
  const capital = parseNum(value);
  if (capital <= 0) return undefined;

  const max = RISK_STEP_MAX_INDIVIDUAL_CAPITAL_BY_BAND[band];
  if (capital > max) {
    return `O Capital Individual não pode ser maior que ${formatCurrency(max)}.`;
  }

  return undefined;
}

/**
 * Piso comercial isolado — mesma independência do catálogo que o teto.
 *
 * Sem banda de propósito: o piso é flat. Um parâmetro provadamente ignorado
 * sugeriria que existe piso por categoria.
 */
export function getIndividualCapitalMinAllowedError(
  value: string
): string | undefined {
  const capital = parseNum(value);
  if (capital <= 0) return undefined;

  if (capital < RISK_STEP_MIN_INDIVIDUAL_CAPITAL) {
    return `O Capital Individual não pode ser menor que ${formatCurrency(RISK_STEP_MIN_INDIVIDUAL_CAPITAL)}.`;
  }

  return undefined;
}

/**
 * Faixa comercial inteira da banda (piso flat + teto da categoria) num erro só,
 * exibido em TEMPO REAL — é o que vai como `alwaysVisibleError`, furando o gate
 * de `submitAttempted`.
 *
 * Por ser recomputado no render, é também o que faz o erro aparecer na hora
 * quando o corretor troca o Tipo de Perfil e o valor digitado passa a estourar
 * o teto da nova categoria.
 *
 * O piso acusa também durante a digitação: `R$ 500,00` a caminho de
 * `R$ 50.000,00` fica vermelho no meio do caminho. É o comportamento pedido —
 * simetria com o teto, que o corretor já vê na hora.
 */
export function getIndividualCapitalRangeError(
  value: string,
  band: CapitalBand
): string | undefined {
  return (
    getIndividualCapitalMaxAllowedError(value, band) ??
    getIndividualCapitalMinAllowedError(value)
  );
}

export function getIndividualCapitalMorteLimitError(
  value: string,
  morteItem: CapitalGlobalCoverageCatalogItem | undefined,
  limits: MorteRiskStepLimits | undefined,
  band: CapitalBand
): string | undefined {
  const capital = parseNum(value);
  if (capital <= 0) return undefined;

  const range = resolveEffectiveIndividualCapitalRange(limits, band);

  // Catálogo pedindo um mínimo acima do teto comercial: não há valor válido.
  // Uma mensagem só, em vez de duas que se contradizem.
  if (range.min > range.max) {
    return `Os limites de Capital Individual são incompatíveis: o mínimo de ${formatCurrency(range.min)} é maior que o máximo de ${formatCurrency(range.max)}.`;
  }

  if (!morteItem || !limits || limits.isRangeInvalid) return undefined;

  if (capital > range.max) {
    return getCapitalExceedsMaxMessage(morteItem, range.max);
  }

  if (capital < range.min) {
    return getCapitalBelowMinMessage(morteItem, range.min);
  }

  return undefined;
}

/**
 * Erro do Capital Individual no perfil UNIFORME, onde o teto depende do Tipo de
 * Perfil.
 *
 * Recebe o `tipoDePerfil` em vez de a banda já resolvida porque o chamador precisa
 * lê-lo do FORM no instante da validação: a revalidação disparada pela troca do
 * radio (`onChangeListenTo`) roda síncrona, antes de o React re-renderizar, e uma
 * banda pré-calculada no render chegaria aqui com o tipo ANTIGO — mantendo na tela
 * exatamente o erro que a troca deveria limpar.
 */
export function getUniformeIndividualCapitalError(
  value: string,
  tipoDePerfil: TipoDePerfil,
  morteContext: MorteRiskValidationContext
): string | undefined {
  const band = resolveCapitalBandFromTipoDePerfil(tipoDePerfil);

  return (
    getIndividualCapitalValidationError(value) ??
    getIndividualCapitalMaxAllowedError(value, band) ??
    getIndividualCapitalMinAllowedError(value) ??
    getIndividualCapitalMorteLimitError(
      value,
      morteContext.morteItem,
      morteContext.limits,
      band
    )
  );
}

// ─── regras que atravessam estipulantes ─────────────────────────────────────

/**
 * Capitais individuais em uso nesta entidade, cada um com a banda que define seu
 * teto (o perfil define quantos são).
 *
 * No ESCALONADO a banda vem do NOME do campo, nunca de `tipoDePerfil`: ali esse
 * campo guarda valor obsoleto (default `'COLABORADORES'`, e as abas restauram o
 * que foi capturado antes), e consultá-lo travaria sócios em 100k silenciosamente.
 */
function getEntityCapitalEntries(
  values: EntityRiskValues
): { value: string; band: CapitalBand }[] {
  if (values.perfilDoGrupo === 'ESCALONADO') {
    return [
      { value: values.sdCapitalIndividual, band: 'partnersAndDirectors' },
      { value: values.colCapitalIndividual, band: 'employees' },
    ];
  }

  return [
    {
      value: values.uniCapitalIndividual,
      band: resolveCapitalBandFromTipoDePerfil(values.tipoDePerfil),
    },
  ];
}

export function sumLivesForEntity(values: EntityRiskValues): number {
  if (values.perfilDoGrupo === 'ESCALONADO') {
    return (
      parseNum(values.sdQuantidadeDeVidas) +
      parseNum(values.colQuantidadeDeVidas)
    );
  }

  return parseNum(values.uniQuantidadeDeVidas);
}

export function sumLivesAcrossEntities(riskDataMap: RiskDataMap): number {
  return Object.values(riskDataMap).reduce(
    (total, values) => total + (values ? sumLivesForEntity(values) : 0),
    0
  );
}

export function getTotalLivesError(totalLives: number): string | undefined {
  if (totalLives <= RISK_STEP_MAX_TOTAL_LIVES) return undefined;

  return `A soma de vidas de todos os estipulantes (${totalLives.toLocaleString('pt-BR')}) excede o limite de ${RISK_STEP_MAX_TOTAL_LIVES.toLocaleString('pt-BR')}.`;
}

export type CapitalViolationKind = 'above' | 'below';

export type EntityCapitalViolation = {
  entityId: string;
  label: string;
  kind: CapitalViolationKind;
  /** Valor permitido que foi violado — varia por banda no `above`. */
  limit: number;
};

/**
 * Estipulantes com Capital Individual fora da faixa comercial, para o toast do
 * "Próximo" nomear quem errou. Campo vazio/zerado não entra — isso é o erro de
 * obrigatoriedade, que já tem canal próprio.
 */
export function findEntitiesWithCapitalOutOfRange(
  riskDataMap: RiskDataMap,
  entities: EntityTab[]
): EntityCapitalViolation[] {
  const violations: EntityCapitalViolation[] = [];

  for (const entity of entities) {
    const values = riskDataMap[entity.id];
    if (!values) continue;

    /*
     * Deduplica por (sentido, limite), não só por sentido: no escalonado as duas
     * bandas podem estourar tetos diferentes, e cada teto precisa virar uma
     * violação própria para o toast citar o número certo em cada linha.
     */
    const seen = new Map<
      string,
      { kind: CapitalViolationKind; limit: number }
    >();
    const add = (kind: CapitalViolationKind, limit: number) =>
      seen.set(`${kind}:${limit}`, { kind, limit });

    for (const entry of getEntityCapitalEntries(values)) {
      const capital = parseNum(entry.value);
      if (capital <= 0) continue;

      const max = RISK_STEP_MAX_INDIVIDUAL_CAPITAL_BY_BAND[entry.band];
      if (capital > max) add('above', max);
      if (capital < RISK_STEP_MIN_INDIVIDUAL_CAPITAL) {
        add('below', RISK_STEP_MIN_INDIVIDUAL_CAPITAL);
      }
    }

    for (const { kind, limit } of seen.values()) {
      violations.push({
        entityId: entity.id,
        label: entity.label,
        kind,
        limit,
      });
    }
  }

  return violations;
}

/**
 * Toast do "Próximo": uma linha por (sentido, limite), nomeando os estipulantes.
 *
 * Agrupar pelo limite é o que mantém o toast honesto — com dois tetos em jogo, um
 * sócio que digitou R$ 250 mil não pode ler "acima do valor permitido
 * (R$ 100.000,00)".
 */
export function buildCapitalOutOfRangeMessages(
  violations: EntityCapitalViolation[]
): string[] {
  const messages: string[] = [];

  for (const kind of ['above', 'below'] as CapitalViolationKind[]) {
    const limits = [
      ...new Set(violations.filter(v => v.kind === kind).map(v => v.limit)),
    ].sort((a, b) => a - b);

    for (const limit of limits) {
      const labels = [
        ...new Set(
          violations
            .filter(v => v.kind === kind && v.limit === limit)
            .map(v => v.label)
        ),
      ];
      const isSingle = labels.length === 1;

      messages.push(
        `${isSingle ? 'O estipulante' : 'Os estipulantes'} ${labels.map(label => `"${label}"`).join(', ')} ${isSingle ? 'preencheu' : 'preencheram'} o Capital Individual ${kind === 'above' ? 'acima' : 'abaixo'} do valor permitido (${formatCurrency(limit)}).`
      );
    }
  }

  return messages;
}

export function getRiskFieldsToValidate(
  perfilDoGrupo: PerfilDoGrupo
): readonly string[] {
  return perfilDoGrupo === 'ESCALONADO'
    ? [
        'sdQuantidadeDeVidas',
        'sdCapitalIndividual',
        'colQuantidadeDeVidas',
        'colCapitalIndividual',
      ]
    : ['uniQuantidadeDeVidas', 'uniCapitalIndividual'];
}

export function isEntityRiskValuesValid(
  values: EntityRiskValues,
  morteContext?: MorteRiskValidationContext
): boolean {
  const isCapitalValid = (capital: string, band: CapitalBand) =>
    getIndividualCapitalValidationError(capital) === undefined &&
    getIndividualCapitalMaxAllowedError(capital, band) === undefined &&
    getIndividualCapitalMinAllowedError(capital) === undefined &&
    (!morteContext ||
      getIndividualCapitalMorteLimitError(
        capital,
        morteContext.morteItem,
        morteContext.limits,
        band
      ) === undefined);

  if (values.perfilDoGrupo === 'ESCALONADO') {
    return (
      getQuantidadeDeVidasValidationError(values.sdQuantidadeDeVidas) ===
        undefined &&
      isCapitalValid(values.sdCapitalIndividual, 'partnersAndDirectors') &&
      getQuantidadeDeVidasValidationError(values.colQuantidadeDeVidas) ===
        undefined &&
      isCapitalValid(values.colCapitalIndividual, 'employees')
    );
  }

  return (
    getQuantidadeDeVidasValidationError(values.uniQuantidadeDeVidas) ===
      undefined &&
    isCapitalValid(
      values.uniCapitalIndividual,
      resolveCapitalBandFromTipoDePerfil(values.tipoDePerfil)
    )
  );
}
